import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '@/lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};

// Simple storage helpers
const storage = {
    get: (key, defaultValue = null) => {
        try {
            const item = localStorage.getItem(key);
            if (!item) return defaultValue;
            // Try to parse as JSON, if fails return raw string
            try {
                return JSON.parse(item);
            } catch {
                return item;
            }
        } catch {
            return defaultValue;
        }
    },
    set: (key, value) => {
        try {
            // Store strings directly, objects as JSON
            const toStore = typeof value === 'string' ? value : JSON.stringify(value);
            localStorage.setItem(key, toStore);
        } catch (e) {
            console.error('LocalStorage error:', e);
        }
    },
    remove: (key) => {
        localStorage.removeItem(key);
    },
};

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(() => storage.get('user'));
    const [token, setToken] = useState(() => localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isLoggingOut, setIsLoggingOut] = useState(false);

    const isAuthenticated = !!user && !!token;

    // Check auth status on mount
    useEffect(() => {
        const checkAuth = async () => {
            if (!token) {
                setLoading(false);
                return;
            }

            try {
                const response = await authAPI.getMe();
                setUser(response.data);
                storage.set('user', response.data);
            } catch (err) {
                // Token invalid, clear auth
                setUser(null);
                setToken(null);
                storage.remove('user');
                storage.remove('token');
            } finally {
                setLoading(false);
            }
        };

        checkAuth();
    }, [token]);

    const login = useCallback(async (email, password) => {
        setError(null);
        try {
            const response = await authAPI.login({ email, password });
            const { access_token, user: userData } = response.data;
            
            // Store token directly as string
            localStorage.setItem('token', access_token);
            storage.set('user', userData);
            setToken(access_token);
            setUser(userData);
            
            return { success: true, user: userData };
        } catch (err) {
            // Check if 2FA is required (status 202)
            if (err.response?.status === 202) {
                const detail = err.response?.data?.detail;
                if (detail?.requires_2fa) {
                    return {
                        requires_2fa: true,
                        temp_token: detail.temp_token,
                        email: detail.email,
                        message: detail.message
                    };
                }
            }
            const message = err.response?.data?.detail || 'Autentificare eșuată';
            setError(typeof message === 'string' ? message : 'Autentificare eșuată');
            throw new Error(typeof message === 'string' ? message : 'Autentificare eșuată');
        }
    }, []);

    const verify2FA = useCallback(async (email, otpCode, tempToken) => {
        setError(null);
        try {
            const response = await authAPI.verify2FA({ email, otp_code: otpCode, temp_token: tempToken });
            const { access_token, user: userData } = response.data;
            
            localStorage.setItem('token', access_token);
            storage.set('user', userData);
            setToken(access_token);
            setUser(userData);
            
            return { success: true, user: userData };
        } catch (err) {
            const message = err.response?.data?.detail || 'Cod incorect';
            setError(message);
            throw new Error(message);
        }
    }, []);

    const resend2FAOTP = useCallback(async (tempToken) => {
        try {
            const response = await authAPI.resend2FAOTP({ temp_token: tempToken });
            return response.data;
        } catch (err) {
            const message = err.response?.data?.detail || 'Eroare la retrimitere';
            throw new Error(message);
        }
    }, []);

    const register = useCallback(async (email, password, username) => {
        setError(null);
        try {
            const response = await authAPI.register({ email, password, username });
            const { access_token, user: userData } = response.data;
            
            // Store token directly as string
            localStorage.setItem('token', access_token);
            storage.set('user', userData);
            setToken(access_token);
            setUser(userData);
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Înregistrare eșuată';
            setError(message);
            throw new Error(message);
        }
    }, []);

    const registerWithInvite = useCallback(async (email, password, username, inviteCode) => {
        setError(null);
        try {
            const response = await authAPI.registerWithInvite({ email, password, username, invite_code: inviteCode });
            const { access_token, user: userData } = response.data;
            
            // Store token directly as string
            localStorage.setItem('token', access_token);
            storage.set('user', userData);
            setToken(access_token);
            setUser(userData);
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Înregistrare eșuată';
            setError(message);
            throw new Error(message);
        }
    }, []);

    const googleLogin = useCallback(async (sessionId) => {
        setError(null);
        try {
            const response = await authAPI.googleSession(sessionId);
            const { access_token, user: userData } = response.data;
            
            // Save token and user data - use localStorage directly like login/register
            if (access_token) {
                localStorage.setItem('token', access_token);
                setToken(access_token);
            }
            storage.set('user', userData);
            setUser(userData);
            
            return userData;
        } catch (err) {
            const message = err.response?.data?.detail || 'Autentificare Google eșuată';
            setError(message);
            throw new Error(message);
        }
    }, []);

    const logout = useCallback(async () => {
        // Set logging out flag BEFORE clearing auth to prevent redirect race condition
        setIsLoggingOut(true);
        localStorage.setItem('isLoggingOut', 'true');
        try {
            await authAPI.logout();
        } catch (err) {
            console.error('Logout error:', err);
        }
        // Clear auth state after the flag is set
        setUser(null);
        setToken(null);
        storage.remove('user');
        storage.remove('token');
        // Reset logging out flag after navigation completes
        setTimeout(() => {
            setIsLoggingOut(false);
            localStorage.removeItem('isLoggingOut');
        }, 500);
    }, []);

    // Set auth data directly (for OAuth callback)
    const setAuthData = useCallback((newToken, userData) => {
        localStorage.setItem('token', newToken);
        storage.set('user', userData);
        setToken(newToken);
        setUser(userData);
    }, []);

    const updateUser = useCallback((updates) => {
        setUser(prev => {
            const updated = { ...prev, ...updates };
            storage.set('user', updated);
            return updated;
        });
    }, []);

    const value = {
        user,
        token,
        isAuthenticated,
        isLoggingOut,
        loading,
        error,
        login,
        verify2FA,
        resend2FAOTP,
        register,
        registerWithInvite,
        googleLogin,
        logout,
        updateUser,
        setError,
        setUser,
        setToken,
        setAuthData,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContext;
